import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { formatDate } from '../../../lib/format';
import type {
  CompanyAgreementRow, CursorPage, HealthReportAccess, MedicalConfigChangeRequest, SettingsPayload, TenantStatus,
} from '@dru/types';
import { BRAND, TENANT_STATUS_LABEL, formatBytes } from '@dru/types';
import {
  AddressBlock, Alert, Button, Card, CardBody, CardHeader, Checkbox, EmptyState,
  ConfirmDialog, ContactBlock, Detail, Dialog, ErrorState, Field, IconAction, InfoTip, RowActions, SelectField,
  StatusBadge, Tabs, Textarea, TextSkeleton, type Tone, cn, useToast,
} from '@dru/ui';
import { Eye, Lock } from 'lucide-react';
import { PageHeader, holds } from '../../../layouts/ConsoleShell';
import { PreferredVenues } from '../components/PreferredVenues';
import { type CompanyPackage, packageUse } from '../lib/companyPackages';
import { api } from '../../../lib/api';
import { groupTests } from '../../../lib/testGroups';
import { useAuth, errorMessage } from '../../../lib/auth';
import { useQuery } from '../../../lib/useQuery';

/**
 * Company settings, and your own preferences.
 *
 * The company's details, plants and medical package are shown, not edited:
 * Dr Ultimate staff keep them (docs/38). What the company still decides here
 * is its health-report policy (`settings.update`) and the venues it prefers;
 * notification preferences are yours, need no permission, and are saved
 * separately.
 */

const HOURS = Array.from({ length: 24 }, (_, h) => h);
const hourLabel = (h: number) => `${String(h).padStart(2, '0')}:00`;

/**
 * A checkbox with its reason underneath.
 *
 * `Checkbox` in the design system is a bare input passthrough, and a
 * notification switch without an explanation is a switch people leave alone —
 * "SMS" says nothing about what does and does not go over it.
 */
function Toggle({
  label, hint, checked, onChange,
}: {
  label: string;
  hint: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div>
      <Checkbox
        label={<span className="font-medium text-neutral-800">{label}</span>}
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      <p className="ml-6 text-caption text-neutral-500">{hint}</p>
    </div>
  );
}

type SettingsTab = 'company' | 'medical' | 'venues' | 'notifications';

/**
 * Four sections, one at a time: the company as Dr Ultimate keeps it, the
 * medical package and who may read reports, the preferred venues, and your
 * own notifications. The section is in the address (`?tab=`), so a link to
 * "your notifications" opens there and Back leaves the section, not the page.
 */
export function Settings() {
  const { permissions } = useAuth();
  // The page reads /settings, which needs settings.read. A role without it
  // (an EHS viewer opening an old link) gets a plain answer, not a 403.
  const canRead = holds(permissions, 'settings.read');
  const settings = useQuery<SettingsPayload | null>(
    () => (canRead ? api.get<SettingsPayload>('/settings') : Promise.resolve(null)), [canRead]);
  const canEdit = holds(permissions, 'settings.update');
  const canVenues = holds(permissions, 'partner.read');
  const [params, setParams] = useSearchParams();

  const asked = params.get('tab') as SettingsTab | null;
  const tab: SettingsTab = asked === 'medical' || asked === 'notifications' || (asked === 'venues' && canVenues)
    ? asked : 'company';
  const setTab = (k: string) => setParams((p) => {
    const n = new URLSearchParams(p);
    if (k === 'company') n.delete('tab'); else n.set('tab', k);
    return n;
  }, { replace: true });

  if (!canRead) {
    return (
      <div className="max-w-3xl">
        <PageHeader title="Settings" />
        <EmptyState
          title="Company settings are not part of your role"
          hint="Ask your Company Admin if you need to see the company's details or change who can read health reports."
        />
      </div>
    );
  }

  return (
    <div className="max-w-3xl space-y-5">
      <PageHeader
        title="Settings"
        hint="Your company's details and medical package, who may read employees' health reports, and how you are notified."
      />

      <Tabs
        tabs={[
          { key: 'company', label: 'Company' },
          { key: 'medical', label: 'Medical & privacy' },
          { key: 'venues', label: 'Preferred venues', visible: canVenues },
          { key: 'notifications', label: 'My notifications' },
        ]}
        active={tab}
        onChange={setTab}
      />

      {settings.error ? (
        <ErrorState title="Settings could not be loaded" detail={settings.error} onRetry={settings.reload} />
      ) : settings.loading || !settings.data ? (
        <Card><CardBody><TextSkeleton /></CardBody></Card>
      ) : tab === 'company' ? (
        <>
          <ManagedNote support={settings.data.support ?? null} />
          <CompanyCard data={settings.data} />
          <CoordinatorCard data={settings.data} />
          <AgreementsCard data={settings.data} />
        </>
      ) : tab === 'medical' ? (
        <>
          {holds(permissions, 'package.read') && (
            <MedicalPackageCard support={settings.data.support ?? null}
                                canRequest={holds(permissions, 'medicalconfig.request')} />
          )}
          <HealthReportPolicyCard data={settings.data} onSaved={settings.reload} canEdit={canEdit} />
        </>
      ) : tab === 'venues' ? (
        <Card><CardBody><PreferredVenues /></CardBody></Card>
      ) : (
        <PreferencesCard data={settings.data} onSaved={settings.reload} />
      )}
    </div>
  );
}

/**
 * Said once, above the three read-only cards, instead of in each card's ⓘ:
 * nothing on this tab is edited here, and this is who to ask.
 */
function ManagedNote({ support }: { support: Support | null }) {
  const contact = supportLine(support);
  return (
    <div className="flex items-start gap-3 rounded-card border border-neutral-200 bg-neutral-50 px-4 py-3 text-small text-neutral-700">
      <Lock aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-neutral-500" />
      <p className="min-w-0">
        <span className="font-medium text-neutral-900">Read only.</span>{' '}
        {BRAND.name} keeps your company's details, contacts and agreements. To correct anything, contact {BRAND.name} support
        {contact ? <>: <span className="break-words font-medium text-neutral-900">{contact}</span></> : '.'}
      </p>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────── */

/**
 * The company as Dr Ultimate has it on file — read only.
 *
 * This was an edit form. The company's details are kept by Dr Ultimate staff
 * now (docs/38), and the API refuses a change from here, so the card shows
 * what is recorded and says who to ask.
 */
function CompanyCard({ data }: { data: SettingsPayload }) {
  const c = data.company;
  return (
    <Card>
      <CardHeader
        title="Company details"
        hint={`Kept by ${BRAND.name}. To change anything here, contact ${BRAND.name} support.`}
      />
      <CardBody className="space-y-4">
        <div className="grid gap-x-8 gap-y-4 sm:grid-cols-2">
          <Detail label="Company name" value={c.name} />
          <Detail label="Legal name" value={c.legalName ?? '—'} />
          <Detail label="GSTIN" value={c.gstin ?? '—'} mono />
          <Detail label="Number of employees" value={c.workforceCount != null ? String(c.workforceCount) : (c.workforceBand ?? 'Not stated')} />
          {/* Not "PREFIX-000001 onwards": a company that imported its own
              codes never had that one. The next number comes from the
              server, which starts past every code already in use. */}
          <Detail label="Employee IDs" value={c.employeeCodePrefix ? `Generated when left blank · next ${c.employeeCodePrefix}-${String(c.employeeCodeNextSeq ?? 1).padStart(6, '0')}` : 'Typed by hand'} mono={Boolean(c.employeeCodePrefix)} />
          <Detail label="Plants" value={String(data.structure.plants)} />
          <Detail label="Status" value={TENANT_STATUS_LABEL[c.status as TenantStatus] ?? c.status} />
          <Detail label="Last changed" value={formatDate(c.updatedAt)} />
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <div className="text-caption text-neutral-500">Registered address</div>
            <AddressBlock address={c} className="mt-1" empty="No address recorded yet." />
          </div>
          <div>
            <div className="text-caption text-neutral-500">Who to call</div>
            <ContactBlock contact={c} className="mt-1" />
          </div>
        </div>
      </CardBody>
    </Card>
  );
}

/**
 * The coordinator Dr Ultimate works with, the group company, and the
 * emergency numbers for the site — read only, like the card above. HR can
 * see what Dr Ultimate has on file, so a wrong ambulance number is noticed
 * by the people who would need it rather than on the day.
 */
function CoordinatorCard({ data }: { data: SettingsPayload }) {
  const c = data.company;
  const dash = (v?: string | null) => v || '—';
  return (
    <Card>
      <CardHeader
        title="Coordinator and emergency contacts"
        hint={`Who ${BRAND.name} arranges camps with, and whom the venue calls in an emergency. Kept by ${BRAND.name} — contact support to correct anything here.`}
      />
      <CardBody className="space-y-4">
        <div className="grid gap-x-8 gap-y-4 sm:grid-cols-2">
          <Detail label="PAN" value={dash(c.pan)} mono={Boolean(c.pan)} />
          <Detail label="Parent company" value={c.parentTenant?.name ?? 'None — not part of a group'} />
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <div className="text-caption text-neutral-500">Coordinator</div>
            {c.coordinatorName || c.coordinatorEmail || c.coordinatorMobile ? (
              <dl className="mt-1 space-y-1 text-body text-neutral-900">
                <div>{[c.coordinatorName, c.coordinatorDesignation].filter(Boolean).join(' · ') || '—'}</div>
                {c.coordinatorMobile && <div className="font-mono">{c.coordinatorMobile}{c.coordinatorAltMobile ? ` · ${c.coordinatorAltMobile}` : ''}</div>}
                {c.coordinatorEmail && <div className="break-all">{c.coordinatorEmail}</div>}
              </dl>
            ) : <p className="mt-1 text-body text-neutral-500">Not recorded yet.</p>}
          </div>
          <div>
            <div className="text-caption text-neutral-500">Emergency numbers</div>
            {c.emergencyAmbulance || c.emergencyFire || c.emergencyPolicePhone || c.emergencyPoliceAddress ? (
              <dl className="mt-1 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-body text-neutral-900">
                <dt className="text-neutral-600">Ambulance</dt><dd className="font-mono">{dash(c.emergencyAmbulance)}</dd>
                <dt className="text-neutral-600">Fire</dt><dd className="font-mono">{dash(c.emergencyFire)}</dd>
                <dt className="text-neutral-600">Police</dt><dd className="font-mono">{dash(c.emergencyPolicePhone)}</dd>
                {c.emergencyPoliceAddress && <><dt className="text-neutral-600">Station</dt><dd>{c.emergencyPoliceAddress}</dd></>}
              </dl>
            ) : <p className="mt-1 text-body text-neutral-500">Not recorded yet.</p>}
          </div>
        </div>
      </CardBody>
    </Card>
  );
}

/**
 * The signed agreements on file, read only. Opening one mints a five-minute
 * grant through the company's own `tenant.read` and is recorded, as every
 * document open on this platform is.
 */
function AgreementsCard({ data }: { data: SettingsPayload }) {
  const rows = data.agreements ?? [];
  const [opening, setOpening] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const today = new Date().toISOString().slice(0, 10);

  const open = async (d: CompanyAgreementRow) => {
    setOpening(d.id); setError(null);
    try {
      const grant = await api.get<{ url: string }>(`/tenants/${data.company.id}/agreements/${d.id}/download`);
      window.open(grant.url, '_blank', 'noopener,noreferrer');
    } catch (e) { setError(errorMessage(e)); } finally { setOpening(null); }
  };

  return (
    <Card>
      <CardHeader
        title="Agreements"
        hint={`Your signed agreements with ${BRAND.name}. Opening one is recorded.`}
      />
      <CardBody>
        {error && <Alert tone="danger" className="mb-3">{error}</Alert>}
        {rows.length === 0 ? (
          <p className="text-body text-neutral-500">No agreements on file yet.</p>
        ) : (
          <ul className="divide-y divide-neutral-100">
            {rows.map((d) => {
              const expired = Boolean(d.validUntil && d.validUntil.slice(0, 10) < today);
              return (
                <li key={d.id} className="flex flex-wrap items-center justify-between gap-3 py-2">
                  <div className="min-w-0">
                    <p className="truncate text-body font-medium text-neutral-900">{d.title}</p>
                    <p className="text-caption text-neutral-500">
                      {d.signedOn ? `Signed ${formatDate(d.signedOn)}` : 'Signing date not recorded'}
                      {d.validUntil ? ` · ${expired ? 'Expired' : 'Valid until'} ${formatDate(d.validUntil)}` : ''}
                      {' · '}{formatBytes(d.sizeBytes)}
                    </p>
                  </div>
                  <RowActions>
                    <IconAction
                      label={opening === d.id ? `Opening ${d.title}` : `Open ${d.title}`}
                      icon={Eye}
                      loading={opening === d.id}
                      onClick={() => open(d)}
                    />
                  </RowActions>
                </li>
              );
            })}
          </ul>
        )}
      </CardBody>
    </Card>
  );
}

type Support = NonNullable<SettingsPayload['support']>;

/** "support@… · +91 … · Mon–Sat 9–6", or nothing when no contact is on file. */
const supportLine = (s: Support | null) =>
  s ? [s.email, s.phone, s.hours].filter(Boolean).join(' · ') : '';

const REQUEST_STATUS: Record<MedicalConfigChangeRequest['status'], { label: string; tone: Tone }> = {
  OPEN: { label: 'Sent', tone: 'pending' },
  IN_REVIEW: { label: 'In review', tone: 'info' },
  RESOLVED: { label: 'Done', tone: 'success' },
  REJECTED: { label: 'Declined', tone: 'neutral' },
};

/**
 * The company's medical packages, read only.
 *
 * Two defaults serve every company (29 Oct 2026): the examination's type
 * picks one. Dr Ultimate may add or remove a test for this company, or make
 * a package of its own; HR sees the result here, and asks for a change in
 * words, which lands on the company's "Change requests" tab in the platform
 * console with the answer coming back under the card.
 */
function MedicalPackageCard({ support, canRequest }: { support: Support | null; canRequest: boolean }) {
  const { toast } = useToast();
  const packages = useQuery<{ items: CompanyPackage[] }>(() => api.get('/company-packages'), []);
  const requests = useQuery<CursorPage<MedicalConfigChangeRequest>>(
    () => api.get('/medical-configurations/change-requests?status=ALL'), []);
  const [asking, setAsking] = useState(false);
  const [open, setOpen] = useState<string | null>(null);
  const items = packages.data?.items ?? [];
  const contact = supportLine(support);
  const recent = (requests.data?.items ?? []).slice(0, 3);
  const pending = (requests.data?.items ?? []).filter((r) => r.status === 'OPEN' || r.status === 'IN_REVIEW').length;
  return (
    <Card>
      <CardHeader
        title="Medical packages"
        hint={`What your employees are examined for. The examination type picks the package; ${BRAND.name} adjusts it for you on request.`}
        actions={canRequest && (
          <div className="flex flex-wrap items-center gap-2">
            {pending > 0 && <StatusBadge tone="pending">{pending} request{pending === 1 ? '' : 's'} open</StatusBadge>}
            <Button size="sm" variant="secondary" onClick={() => setAsking(true)}>Request a change</Button>
          </div>
        )}
      />
      <CardBody>
        {packages.error ? (
          <ErrorState detail={packages.error} onRetry={packages.reload} />
        ) : packages.loading && !packages.data ? (
          <TextSkeleton />
        ) : !items.length ? (
          <Alert tone="warning" title="No package available">
            No medical package is available to your company yet. Please contact {BRAND.name} support
            {contact ? <>: <span className="break-words">{contact}</span></> : '.'}
          </Alert>
        ) : (
          <ul className="divide-y divide-neutral-100 rounded-card border border-neutral-200">
            {items.map((p) => {
              const added = p.tests.filter((t) => t.added).length;
              const expanded = open === p.id;
              return (
                <li key={p.id}>
                  <button
                    type="button"
                    aria-expanded={expanded}
                    onClick={() => setOpen(expanded ? null : p.id)}
                    className="flex w-full items-start gap-3 px-4 py-3 text-left hover:bg-neutral-50 [@media(pointer:coarse)]:min-h-14"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-body font-medium text-neutral-900">{p.name}</span>
                        {p.own && <StatusBadge tone="neutral">Company package</StatusBadge>}
                        {!p.isActive && <StatusBadge tone="neutral">Retired</StatusBadge>}
                      </div>
                      <div className="mt-0.5 text-caption text-neutral-600">{packageUse(p)}</div>
                    </div>
                    <div className="shrink-0 text-right text-caption text-neutral-600">
                      <div className="font-medium text-neutral-800">{p.tests.length} test{p.tests.length === 1 ? '' : 's'}</div>
                      {(added > 0 || p.removed.length > 0) && (
                        <div className="text-primary-700">
                          {[added ? `${added} added` : null, p.removed.length ? `${p.removed.length} removed` : null].filter(Boolean).join(' · ')} for you
                        </div>
                      )}
                      <div className="mt-0.5 text-primary-600">{expanded ? 'Hide tests' : 'Show tests'}</div>
                    </div>
                  </button>
                  {expanded && (
                    <div className="px-4 pb-4">
                      <div className="space-y-3">
                        {groupTests(p.tests).map((g) => (
                          <div key={g.key}>
                            <div className="mb-0.5 text-caption font-semibold uppercase tracking-wide text-neutral-600">
                              {g.label} <span className="font-normal normal-case tracking-normal text-neutral-500">· {g.tests.length}</span>
                            </div>
                            <ul className="grid gap-x-6 gap-y-1 text-small text-neutral-800 sm:grid-cols-2 lg:grid-cols-3">
                              {g.tests.map((t) => (
                                <li key={t.id} className="min-w-0 break-words">
                                  {t.name}
                                  {t.added && <span className="ml-1.5 text-caption font-medium text-primary-700">added for you</span>}
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                      {p.removed.length > 0 && (
                        <p className="mt-3 text-caption text-neutral-600">
                          Removed for your company:{' '}
                          <span className="text-neutral-800 line-through decoration-neutral-400">{p.removed.map((t) => t.name).join(', ')}</span>
                        </p>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}

        <p className="mt-3 text-caption text-neutral-500">
          The tests are set by {BRAND.name}'s physicians, not chosen here.
          {canRequest ? ' Use "Request a change" to ask for one to be added or removed.' : ' Ask your Company Admin to request a change.'}
        </p>

        {requests.error ? (
          <p className="mt-4 text-caption text-neutral-500">Your earlier requests could not be loaded.</p>
        ) : recent.length > 0 && (
          <div className="mt-4 border-t border-neutral-100 pt-3">
            <div className="text-caption text-neutral-500">Your requests</div>
            <ul className="mt-1 space-y-2">
              {recent.map((r) => (
                <li key={r.id} className="text-small">
                  <div className="flex flex-wrap items-center gap-2">
                    <StatusBadge tone={REQUEST_STATUS[r.status].tone}>{REQUEST_STATUS[r.status].label}</StatusBadge>
                    <span className="min-w-0 text-neutral-800">{r.subject}</span>
                    <span className="text-caption text-neutral-500">{formatDate(r.createdAt)}</span>
                  </div>
                  {r.response && <p className="mt-0.5 text-caption text-neutral-600">{BRAND.name}: {r.response}</p>}
                </li>
              ))}
            </ul>
          </div>
        )}

        {contact && (
          <p className="mt-4 text-caption text-neutral-500">
            {BRAND.name} support: <span className="break-words text-neutral-700">{contact}</span>
          </p>
        )}
      </CardBody>
      {asking && (
        <ChangeRequestDialog
          config={null}
          subject="Change to our medical packages"
          onClose={() => setAsking(false)}
          onSent={() => { setAsking(false); requests.reload(); toast('success', `Request sent to ${BRAND.name}. Their reply will show here.`); }}
        />
      )}
    </Card>
  );
}

/**
 * "Please add an audiometry for the welders": one free-text message. The
 * subject the platform's list needs is made from the package's name, so HR
 * is asked one thing, not two.
 */
function ChangeRequestDialog({ config, subject, onClose, onSent }: {
  config: { id: string; name: string } | null;
  subject?: string;
  onClose: () => void;
  onSent: () => void;
}) {
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [idempotencyKey] = useState(() => crypto.randomUUID());
  const short = message.trim().length < 10;

  const send = async () => {
    setBusy(true); setError(null);
    try {
      await api.post('/medical-configurations/change-requests', {
        subject: (subject ?? (config ? `Change to ${config.name}` : 'Set up our medical package')).slice(0, 160),
        message: message.trim(),
        ...(config ? { configId: config.id } : {}),
      }, idempotencyKey);
      onSent();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      title="Request a change"
      info={`Goes to ${BRAND.name}, who change the package and reply here. Nothing changes until they do.`}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button onClick={send} loading={busy} disabled={short}>Send request</Button>
        </>
      }
    >
      {error && <Alert tone="danger" className="mb-3">{error}</Alert>}
      <Field label="What should change?" info="For example: add a hearing test for welders.">
        {(f) => (
          <Textarea {...f} rows={5} maxLength={2000} autoFocus value={message}
                    onChange={(e) => setMessage(e.target.value)} />
        )}
      </Field>
    </Dialog>
  );
}

/**
 * Who in the company may read a worker's Health Check Report.
 *
 * Kept out of the profile form on purpose. It sat as one select among the
 * GSTIN and the headcount, where "save the new phone number" also re-sent a
 * clinical-privacy policy. It is a decision about workers' medical data, the
 * worker's consent notice describes it, and the change has its own audit
 * row — so it gets its own card, the three options spelled out, and a
 * confirmation that says who will see more or less the moment it saves.
 */
const POLICY: Record<HealthReportAccess, { title: string; body: string }> = {
  FULL: {
    title: 'Full report',
    body: 'HR users who may read health reports can open the whole document, test values included.',
  },
  SUMMARY: {
    title: 'Summary only',
    body: 'They see the fitness decision and counts of results in and out of range — no test values.',
  },
  NONE: {
    title: 'Certificate only',
    body: 'No health report reaches HR. They see the fitness certificate and nothing clinical beyond it.',
  },
};
const ORDER: HealthReportAccess[] = ['FULL', 'SUMMARY', 'NONE'];

function HealthReportPolicyCard({
  data, onSaved, canEdit,
}: { data: SettingsPayload; onSaved: () => void; canEdit: boolean }) {
  const current = (data.company.healthReportAccess ?? 'FULL') as HealthReportAccess;
  const [choice, setChoice] = useState<HealthReportAccess>(current);
  const [confirming, setConfirming] = useState(false);
  const { toast } = useToast();
  useEffect(() => setChoice(current), [current]);

  const widens = ORDER.indexOf(choice) < ORDER.indexOf(current);

  return (
    <Card className="border-warning-600/30">
      <CardHeader
        title="Health report access"
        hint="A clinical-privacy policy: how much of an employee's Health Check Report your HR users can read. It applies to everyone whose role includes health-report access — by default Company Admins, Plant HR and EHS officers."
      />
      <CardBody className="space-y-4">
        <fieldset disabled={!canEdit}>
          <legend className="sr-only">Health report access</legend>
          <ul className="space-y-2">
            {ORDER.map((k) => (
              <li key={k}>
                <label
                  className={cn(
                    'flex gap-3 rounded-input border p-3',
                    canEdit && 'cursor-pointer',
                    choice === k ? 'border-primary-500 bg-primary-50' : 'border-neutral-200',
                    canEdit && choice !== k && 'hover:bg-neutral-50',
                  )}
                >
                  <input
                    type="radio"
                    name="healthReportAccess"
                    value={k}
                    checked={choice === k}
                    onChange={() => setChoice(k)}
                    className="mt-1 h-4 w-4 shrink-0 border-neutral-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="min-w-0">
                    <span className="block text-body font-medium text-neutral-900">
                      {POLICY[k].title}
                      {k === current && <span className="ml-2 text-caption font-normal text-neutral-500">(current)</span>}
                    </span>
                    <span className="block text-small text-neutral-600">{POLICY[k].body}</span>
                  </span>
                </label>
              </li>
            ))}
          </ul>
        </fieldset>

        <p className="flex items-center gap-1 text-small text-neutral-500">
          Every report opened is recorded in the audit trail.
          <InfoTip label="About health report access">
            Employees' consent notice says their report may be shared with the occupational health
            team. Physicians and the employee themselves always see the full report; this setting
            does not change that.
          </InfoTip>
        </p>

        {canEdit ? (
          <div className="flex flex-wrap items-center justify-end gap-3">
            {choice !== current && (
              <Button variant="ghost" onClick={() => setChoice(current)}>Keep current</Button>
            )}
            <Button
              variant={choice === current ? 'secondary' : 'primary'}
              disabled={choice === current}
              onClick={() => setConfirming(true)}
            >
              Change policy…
            </Button>
          </div>
        ) : (
          <p className="text-small text-neutral-500">Only a Company Admin can change this policy.</p>
        )}
      </CardBody>

      {confirming && (
        <ConfirmDialog
          title="Change who can read health reports?"
          what={<>From <strong>{POLICY[current].title}</strong> to <strong>{POLICY[choice].title}</strong>. {POLICY[choice].body}</>}
          affects={widens
            ? 'Takes effect at once for every report already issued, not only new ones: HR users will see more clinical detail than they could a moment ago.'
            : 'Takes effect at once: HR users lose access to detail they could read a moment ago, including on reports already issued.'}
          irreversible="The change is recorded in the audit trail with the previous setting."
          confirmLabel="Change policy"
          tone={widens ? 'danger' : 'primary'}
          onConfirm={async () => {
            await api.patch('/settings', { healthReportAccess: choice });
            setConfirming(false);
            toast('success', `Health report access is now "${POLICY[choice].title}".`);
            onSaved();
          }}
          onCancel={() => setConfirming(false)}
        />
      )}
    </Card>
  );
}

function PreferencesCard({
  data, onSaved,
}: { data: SettingsPayload; onSaved: () => void }) {
  const p = data.preferences;
  const [email, setEmail] = useState(p.emailEnabled);
  const [sms, setSms] = useState(p.smsEnabled);
  const [quiet, setQuiet] = useState(p.quietFrom !== null && p.quietUntil !== null);
  const [from, setFrom] = useState(String(p.quietFrom ?? 22));
  const [until, setUntil] = useState(String(p.quietUntil ?? 6));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  const sameHour = quiet && from === until;
  const dirty = email !== p.emailEnabled || sms !== p.smsEnabled
    || quiet !== (p.quietFrom !== null && p.quietUntil !== null)
    || (quiet && (Number(from) !== p.quietFrom || Number(until) !== p.quietUntil));

  const save = async () => {
    setBusy(true); setError(null);
    try {
      await api.patch('/settings/preferences', {
        emailEnabled: email,
        smsEnabled: sms,
        quietFrom: quiet ? Number(from) : null,
        quietUntil: quiet ? Number(until) : null,
      });
      toast('success', 'Your notification preferences are saved.');
      onSaved();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <CardHeader
        title="Your notifications"
        hint="Yours alone — this does not change what your colleagues receive."
      />
      <CardBody className="space-y-4">
        {error && <Alert tone="danger">{error}</Alert>}

        <Toggle
          label="Email" checked={email} onChange={setEmail}
          hint="Compliance alerts, campaign progress and expiry reminders."
        />
        <Toggle
          label="SMS" checked={sms} onChange={setSms}
          hint="Only operational prompts. Medical detail is never sent by SMS."
        />
        <Toggle
          label="Quiet hours" checked={quiet} onChange={setQuiet}
          hint="Nothing non-urgent is sent during this window. It may cross midnight."
        />

        {quiet && (
          <div className="grid gap-4 sm:grid-cols-2">
            <SelectField label="From" value={from} onChange={setFrom}>
              {HOURS.map((h) => (
                <option key={h} value={String(h)}>{hourLabel(h)}</option>
              ))}
            </SelectField>
            <SelectField label="Until" value={until} onChange={setUntil}>
              {HOURS.map((h) => (
                <option key={h} value={String(h)}>{hourLabel(h)}</option>
              ))}
            </SelectField>
          </div>
        )}
        {sameHour && (
          <p role="alert" className="text-small text-danger-600">The quiet window must start and end at different hours.</p>
        )}
        {quiet && !sameHour && (
          <p className="text-caption text-neutral-500">
            Quiet from {hourLabel(Number(from))} to {hourLabel(Number(until))}{Number(from) > Number(until) ? ' the next morning' : ''}.
          </p>
        )}

        <p className="text-caption text-neutral-500">
          Everything also arrives in your in-app inbox (the bell), whatever you choose here.
        </p>

        <div className="flex flex-wrap items-center justify-end gap-3">
          {!dirty && <span className="text-caption text-neutral-500">No unsaved changes</span>}
          <Button loading={busy} disabled={!dirty || sameHour} onClick={save}>Save preferences</Button>
        </div>
      </CardBody>
    </Card>
  );
}
